import { DateTime } from "luxon";

import {
  RC_PACESETTER_KY_FAX_NUMBER,
  RC_PACESETTER_KY_FAX_NUMBER_2,
  RC_PACESETTER_CO_FAX_NUMBER,
  RC_PACESETTER_PA_FAX_NUMBER,
} from "@/configuration/groups/pacesetter/fax-numbers";
import { buildPhysicianNameIndex, matchPhysicianByName } from "@/configuration/physician-roster-name-match";
import { DocRoutingStrategy } from "@/src/lib/constants";
import { logger } from "@/src/lib/logging/logger";
import { isMinorDateOfBirth, parseFlexibleDateStringToDateTime } from "@/src/lib/utils/date-and-phone-utils";
import { Classification } from "@/src/models/constants/classifications";
import type { CommonProcedureTerminologyItem } from "@/src/models/documents/prior-authorization/prior-authorization";
import { Practitioner, getProviderNameWithCredentials } from "@/src/models/practitioner";
import { requireUploadClassification, Section } from "@/src/models/section";

// Non-referral document forwarding targets.
// KY non-referrals are faxed to this number; CO non-referrals are emailed.
export const PACESETTER_KY_NON_REFERRAL_FAX_NUMBER = "+15024490108";
export const PACESETTER_CO_NON_REFERRAL_EMAIL = "lauren@pacesetter-health.com";

/** Returns email recipients for non-referral documents on the CO line, empty array otherwise. */
export function getPacesetterNonReferralEmailRecipients(faxRecipientNumber: string | null): string[] {
  if (faxRecipientNumber === RC_PACESETTER_CO_FAX_NUMBER) {
    return [PACESETTER_CO_NON_REFERRAL_EMAIL];
  }

  return [];
}

export enum PacesetterFaxLine {
  PA = "PA",
  KY = "KY",
}

const PACESETTER_FAX_NUMBER_TO_LINE: Record<string, PacesetterFaxLine> = {
  [RC_PACESETTER_PA_FAX_NUMBER]: PacesetterFaxLine.PA,
  [RC_PACESETTER_KY_FAX_NUMBER]: PacesetterFaxLine.KY,
  [RC_PACESETTER_KY_FAX_NUMBER_2]: PacesetterFaxLine.KY,
  [RC_PACESETTER_CO_FAX_NUMBER]: PacesetterFaxLine.PA,
};

const PACESETTER_ASSIGNEE_BY_LINE: Record<PacesetterFaxLine, string> = {
  [PacesetterFaxLine.PA]: "PA Central Scheduling",
  [PacesetterFaxLine.KY]: "KY-Ready To Schedule",
};

const PACESETTER_FACILITY_BY_LINE: Record<PacesetterFaxLine, string> = {
  [PacesetterFaxLine.PA]: "PA Podiatry Associates Inc.",
  [PacesetterFaxLine.KY]: "Kentuckiana Foot & Ankle, PLLC",
};

// Analytics departments = the two live inbound fax lines (source: Pacesetter Index Mapping).
// department_id is derived from the raw inbound fax number normalized to E.164, so the ids MUST
// be the E.164 fax numbers (matched exactly by the analytics filter); the values are display names.
const PACESETTER_DEPARTMENT_NAMES: Record<string, string> = {
  [RC_PACESETTER_CO_FAX_NUMBER]: "Colorado (PA)",
  [RC_PACESETTER_KY_FAX_NUMBER_2]: "Kentucky (KY)",
};

export function getPacesetterDepartmentIds(): string[] {
  return Object.keys(PACESETTER_DEPARTMENT_NAMES);
}

export function getPacesetterDepartmentName(departmentId: string): string | null {
  return PACESETTER_DEPARTMENT_NAMES[departmentId] ?? null;
}

// Payers that require prior auth on the KY line — drives the "Authorization needed" / PCP-mismatch
// flags. Source: Pacesetter Index Mapping — Insurances (Authorization Needed - KY). Matched on
// distinctive keywords (like Balance Health) so decorated/varied extractions still hit.
function isPacesetterKyAuthRequired(payer: string | null): boolean {
  const p = payer?.toLowerCase().trim() ?? "";

  if (!p) return false;

  // Categories: any HMO (incl. Dual Advantage HMO); any Medicaid except straight KY Medicaid.
  if (p.includes("hmo")) return true;
  if (p.includes("medicaid") && !p.includes("kentucky medicaid") && !p.includes("ky medicaid")) return true;

  // Named plans that don't carry "HMO"/"Medicaid" in the name.
  if (p === "va" || p.startsWith("va ") || p.includes("veterans")) return true; // too short for substring
  if (p.includes("tricare prime")) return true;
  if (p.includes("passport")) return true;
  if (p.includes("ambetter") || p.includes("wellcare")) return true;
  if (p.includes("aetna better health")) return true;
  if ((p.includes("united health") || p.includes("unitedhealth")) && p.includes("community")) return true;

  return false;
}

// HMO plans on the CO line require prior auth — drives the "Authorization needed" flag for Colorado faxes.
// Source: Pacesetter Index Mapping — Insurances (Authorization Needed - CO).
function isPacesetterCoAuthRequired(payer: string | null): boolean {
  const p = payer?.toLowerCase().trim() ?? "";

  return p.length > 0 && p.includes("hmo");
}

// Payers Pacesetter does not accept — drives the "Insurance not accepted" flag (PA and KY lines only).
// CO has no not-accepted list; the check is skipped for that fax number.
// Source: Pacesetter Index Mapping — Insurances (Not Accepted).
function isPacesetterNotAcceptedPayer(payer: string | null): boolean {
  const p = payer?.toLowerCase().trim() ?? "";

  if (!p) return false;
  if (p.includes("anthem silver pathway")) return true;
  if (p.includes("aarp")) return true;
  if (p.includes("health administration") || p.includes("uofl")) return true;
  if (p.includes("department of labor") || p.includes("work comp")) return true;
  if (p.includes("devoted hmo")) return true;

  return false;
}

// Provider roster by NPI, mirroring Balance Health's BALANCE_HEALTH_PROVIDERS_BY_NPI pattern
// (display/reference lookup only — this does NOT gate extraction; receivingProvider blocks are
// already roster-only by default via the approved-Practitioner DB matching in src/lib/blocks/,
// unrelated to this constant).
const PACESETTER_PROVIDERS_BY_NPI: Readonly<Record<string, { physician: string; location: PacesetterFaxLine }>> = {
  "1184905887": { physician: "Oase, Brian, DPM", location: PacesetterFaxLine.PA },
  "1154705689": { physician: "Allen, Lindsy, DPM", location: PacesetterFaxLine.PA },
  "1366490666": { physician: "Daniel, Charles, DPM", location: PacesetterFaxLine.PA },
  "1316473408": { physician: "Scotten, Chris, DPM", location: PacesetterFaxLine.PA },
  "1093713265": { physician: "Rosenthall, Eugene, DPM", location: PacesetterFaxLine.PA },
  "1619331931": { physician: "Neidecker, Katie, DPM", location: PacesetterFaxLine.PA },
  "1982019295": { physician: "Thompson, Patrick, DPM", location: PacesetterFaxLine.PA },
  "1205276920": { physician: "Schamber, Jane, DPT", location: PacesetterFaxLine.PA },
  "1356038376": { physician: "Henderson, Adam", location: PacesetterFaxLine.KY },
  "1841272721": { physician: "Khader, Syed, DPM", location: PacesetterFaxLine.KY },
  "1285956706": { physician: "Klutts, Paul, DPM", location: PacesetterFaxLine.KY },
  "1487691515": { physician: "Lemmenes, Ryan, DPM", location: PacesetterFaxLine.KY },
  "1194254193": { physician: "Millar, Sara, DPM", location: PacesetterFaxLine.KY },
  "1760111504": { physician: "Naveed, Anooshay, DPM", location: PacesetterFaxLine.KY },
  "1760836100": { physician: "Seyal, Mehvish, DPM", location: PacesetterFaxLine.KY },
  "1306893599": { physician: "Unroe, Bradford, DPM", location: PacesetterFaxLine.KY },
  "1992239214": { physician: "Winters, Nathaniel, DPM", location: PacesetterFaxLine.KY },
};

export function getPacesetterProviderByNpi(
  npi: string | null
): { physician: string; location: PacesetterFaxLine } | null {
  return npi ? (PACESETTER_PROVIDERS_BY_NPI[npi] ?? null) : null;
}

const PACESETTER_PHYSICIAN_NAME_INDEX = buildPhysicianNameIndex(
  Object.values(PACESETTER_PROVIDERS_BY_NPI).map(({ physician }) => physician)
);

function getPacesetterFaxLine(faxRecipientNumber: string | null): PacesetterFaxLine {
  const line = faxRecipientNumber ? PACESETTER_FAX_NUMBER_TO_LINE[faxRecipientNumber] : undefined;

  if (!line) {
    logger.warn("Pacesetter referral: unrecognized fax recipient number, defaulting to PA line", {
      faxRecipientNumber,
    });

    return PacesetterFaxLine.PA;
  }

  return line;
}

export function getPacesetterReferralAssignee(faxRecipientNumber: string | null): string {
  return PACESETTER_ASSIGNEE_BY_LINE[getPacesetterFaxLine(faxRecipientNumber)];
}

export function getPacesetterFacility(faxRecipientNumber: string | null): string {
  return PACESETTER_FACILITY_BY_LINE[getPacesetterFaxLine(faxRecipientNumber)];
}

function getStringField(content: Record<string, unknown>, key: string): string | null {
  const value = content[key];

  return typeof value === "string" && value.length > 0 ? value : null;
}

function joinNoteParts(parts: Array<string | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

// Dots (YYYY.MM.DD) to match formatDateWithDots, the format the actual ModMed task/document
// label uses (src/lib/utils/date-and-phone-utils.ts, via renderTemplate/resolveDocumentLabel).
export function formatPacesetterDate(dateString: string | null | undefined): string | null {
  if (!dateString) return null;

  const parsed = parseFlexibleDateStringToDateTime(dateString);

  return parsed?.isValid ? parsed.toFormat("yyyy.MM.dd") : dateString;
}

// Auth end date defaults to start date + 1 year when not extracted.
function resolvePacesetterAuthEndDate(startDate: string | null, endDate: string | null): string | null {
  if (endDate) return endDate;
  if (!startDate) return null;

  const parsed = DateTime.fromISO(startDate);

  return parsed.isValid ? parsed.plus({ years: 1 }).toISODate() : null;
}

function formatCptCodes(list: CommonProcedureTerminologyItem[] | null | undefined): string | null {
  if (!list || list.length === 0) return null;

  return list.map((item) => item.code).join(", ");
}

function getFirstDiagnosisText(reviewedContent: Record<string, unknown>): string | null {
  const diagnosisList = reviewedContent.diagnosisList as Array<{ diagnosisText: string | null }> | null | undefined;

  return diagnosisList?.find((d) => !!d.diagnosisText)?.diagnosisText ?? null;
}

function getPractitionerNpi(practitioner: Practitioner | null | undefined): string | null {
  return practitioner?.npiNumber || null;
}

export function getPacesetterSchedulingPriority(reviewedContent: Record<string, unknown>): string {
  return getStringField(reviewedContent, "schedulingPriority") ?? "Routine";
}

export type PacesetterAuthorizationDetails = {
  type: string;
  authorizationNumber: string | null;
  fromDate: string | null;
  toDate: string | null;
  numberOfVisits: number | null;
  description: string | null;
  assignedProviderText: string | null;
  referringProviderText: string | null;
  referringProviderNpi: string | null;
};

/**
 * Authorization section fields for the ModMed sync screen. Shared by the CloudCruise
 * task-create step and the review-screen UI so both reflect the same data.
 */
export function getPacesetterAuthorizationDetails(
  section: Section,
  receivingProvider: Practitioner | null | undefined
): PacesetterAuthorizationDetails {
  const reviewedContent = (section.reviewedContent ?? {}) as Record<string, unknown>;
  const referringProvider = reviewedContent.referringProvider as Practitioner | null | undefined;

  const requestId = getStringField(reviewedContent, "requestId");
  const referringProviderNpi = getPractitionerNpi(referringProvider);

  const startDate = getStringField(reviewedContent, "startDate");
  const endDate = resolvePacesetterAuthEndDate(startDate, getStringField(reviewedContent, "endDate"));

  const diagnosis = getFirstDiagnosisText(reviewedContent);
  const cptCodes = formatCptCodes(
    reviewedContent.commonProcedureTerminologyList as CommonProcedureTerminologyItem[] | null | undefined
  );
  const description = joinNoteParts([diagnosis, cptCodes ? `CPT codes: ${cptCodes}` : null]) || null;

  const assignedProviderNpi = getPractitionerNpi(receivingProvider);
  const rosterMatch = getPacesetterProviderByNpi(assignedProviderNpi);
  // Prefer the canonical NPI-keyed name; when the document has no usable NPI, try a name match
  // against the roster before falling back to the raw extracted name (no suffix/credential).
  const assignedProviderName =
    rosterMatch?.physician ??
    matchPhysicianByName(
      PACESETTER_PHYSICIAN_NAME_INDEX,
      receivingProvider?.familyName,
      receivingProvider?.givenName
    ) ??
    (receivingProvider ? getProviderNameWithCredentials(receivingProvider) : null);
  const referringProviderName = referringProvider ? getProviderNameWithCredentials(referringProvider) : null;

  return {
    type: "Referral",
    authorizationNumber: requestId || referringProviderNpi,
    fromDate: startDate,
    toDate: endDate,
    numberOfVisits: typeof reviewedContent.numberOfServices === "number" ? reviewedContent.numberOfServices : null,
    description,
    assignedProviderText: assignedProviderName
      ? joinNoteParts([assignedProviderName, assignedProviderNpi ? `(NPI: ${assignedProviderNpi})` : null])
      : null,
    referringProviderText: referringProviderName,
    referringProviderNpi,
  };
}

export type PacesetterStickyNoteFlags = {
  authorizationNeeded: boolean;
  pcpMismatch: boolean;
  insuranceNotAccepted: boolean;
  minorPatient: boolean;
};

export function getPacesetterStickyNoteFlags(
  section: Section,
  faxRecipientNumber: string | null,
  patientDateOfBirth: string | null = null
): PacesetterStickyNoteFlags {
  const reviewedContent = (section.reviewedContent ?? {}) as Record<string, unknown>;

  const payer = (reviewedContent.primaryCoverage as { payer: string | null } | null | undefined)?.payer ?? null;

  // CO line has no not-accepted list — skip the check entirely for that fax number.
  // Not-accepted takes precedence — if we don't accept the plan, auth/PCP flags are moot.
  const isCoLine = faxRecipientNumber === RC_PACESETTER_CO_FAX_NUMBER;
  const notAccepted = !isCoLine && isPacesetterNotAcceptedPayer(payer);
  const isKyLine = getPacesetterFaxLine(faxRecipientNumber) === PacesetterFaxLine.KY;
  const authRequired =
    !notAccepted &&
    ((isKyLine && isPacesetterKyAuthRequired(payer)) || (isCoLine && isPacesetterCoAuthRequired(payer)));

  const requestId = getStringField(reviewedContent, "requestId");
  const referringProvider = reviewedContent.referringProvider as Practitioner | null | undefined;
  const primaryCareProvider = reviewedContent.primaryCareProvider as Practitioner | null | undefined;
  // Only a real mismatch when both NPIs are present and differ — a missing PCP NPI isn't a mismatch.
  const pcpNpi = getPractitionerNpi(primaryCareProvider);
  const referringNpi = getPractitionerNpi(referringProvider);
  const pcpMismatch = !!pcpNpi && !!referringNpi && pcpNpi !== referringNpi;

  return {
    authorizationNeeded: authRequired && !requestId,
    pcpMismatch: isKyLine && authRequired && pcpMismatch,
    insuranceNotAccepted: notAccepted,
    minorPatient: isMinorDateOfBirth(patientDateOfBirth),
  };
}

function formatReferringProviderAndFax(referringProviderName: string | null, faxLine: string | null): string {
  const referredByPart = referringProviderName ? `Referred by: ${referringProviderName}` : null;
  const faxPart = faxLine ? `- Fax: ${faxLine}` : null;

  return joinNoteParts([referredByPart, faxPart]);
}

/**
 * Sticky note text for the ModMed task, matching the Pacesetter sync-screen template.
 * Shared by the CloudCruise task-create step and the review-screen UI so both stay in sync.
 */
export function getPacesetterStickyNote(
  section: Section,
  faxRecipientNumber: string | null,
  patientDateOfBirth: string | null = null
): string {
  const reviewedContent = (section.reviewedContent ?? {}) as Record<string, unknown>;

  const provider = reviewedContent.referringProvider as Practitioner | null | undefined;
  const referringProviderName = provider ? getProviderNameWithCredentials(provider) : null;

  const referringLocation = reviewedContent.referringLocation as
    | { name: string | null; faxPhoneNumber: string | null }
    | null
    | undefined;
  const referringLocationFax = referringLocation?.faxPhoneNumber ?? null;
  const referringLocationName = referringLocation?.name?.trim() || null;

  const startDate = getStringField(reviewedContent, "startDate");
  const endDate = resolvePacesetterAuthEndDate(startDate, getStringField(reviewedContent, "endDate"));
  const authDateLine =
    startDate || endDate ? `${formatPacesetterDate(startDate) ?? ""} - ${formatPacesetterDate(endDate) ?? ""}` : null;

  const primaryCoverage = reviewedContent.primaryCoverage as
    | { payer: string | null; subscriberId: string | null }
    | null
    | undefined;
  const insuranceLine = joinNoteParts([primaryCoverage?.payer, primaryCoverage?.subscriberId]) || null;

  const reasonLine = getStringField(reviewedContent, "reasonForReferral");

  const authId = getStringField(reviewedContent, "requestId");

  const flags = getPacesetterStickyNoteFlags(section, faxRecipientNumber, patientDateOfBirth);
  const flagLines = [
    flags.minorPatient ? "Minor patient" : null,
    flags.insuranceNotAccepted ? "Insurance not accepted" : null,
    flags.authorizationNeeded ? "Authorization needed" : null,
    flags.pcpMismatch ? "Insurance PCP does not match referring provider" : null,
  ];

  return [
    formatReferringProviderAndFax(referringProviderName, referringLocationFax),
    referringLocationName ? `Facility: ${referringLocationName}` : null,
    authDateLine,
    insuranceLine,
    reasonLine,
    authId,
    ...flagLines,
  ]
    .filter(Boolean)
    .join("\n");
}

// Referrals go through V2 (INBOUND_REFERRAL_WF) and never reach here.
// Non-referrals: KY line → fax forward, CO line → email, PA line → no routing.
export function getPacesetterDocRoutingStrategy(
  section: Section,
  faxRecipientNumber: string | null
): DocRoutingStrategy[] {
  const pacesetterClassification = requireUploadClassification(section);

  if (pacesetterClassification === Classification.REFERRAL) return [];
  if (faxRecipientNumber === RC_PACESETTER_KY_FAX_NUMBER || faxRecipientNumber === RC_PACESETTER_KY_FAX_NUMBER_2) {
    return [DocRoutingStrategy.FAX];
  }

  if (faxRecipientNumber === RC_PACESETTER_CO_FAX_NUMBER) {
    return [DocRoutingStrategy.EMAIL];
  }

  return [];
}
